import { Card } from '../components/ui/Card';
import { CodeSnippet } from '../components/ui/CodeSnippet';
import { TechSelector } from '../components/ui/TechSelector';

import { useAccountNumber } from '/src/hooks';
import { VideoPlayer } from '/src/components/VideoPlayer';
import { ListItem } from '../components/ui/ListItem';
import { SupportLink } from '../../_global/components/SupportLink';
import { SupportLinks } from '/src/support';
import JoomlaPoster from '/src/features/installation/assets/joomla-instructions-poster.jpg';
import JoomlaVideo from '/src/features/installation/assets/joomla-instructions.mp4';

export function JoomlaInstructions({ techOptions = [], selectedTechOption = null, onSelectTech }) {
  const [accNumber] = useAccountNumber(false);

  return (
    <Card className="max-w-[720px] w-full mt-5 mb-6">
      <div className="mb-4 flex w-full flex-col gap-3 md:flex-row md:items-center">
        <h2 className="flex-1 text-lg font-bold leading-9 text-black-pearl-500">Instructions</h2>
        <TechSelector techOptions={techOptions} selectedTechOption={selectedTechOption} onSelectTech={onSelectTech} />
      </div>

      <ol className="flex flex-col gap-4 mb-4">
        <ListItem number={1}>
          Log in to your Joomla account, and open your <strong>Control Panel.</strong>
        </ListItem>

        <ListItem number={2}>
          Go to <strong>Extensions &gt; Manage &gt; Install &gt; Install from URL.</strong>
        </ListItem>

        <ListItem number={3}>
          Enter the Crazy Egg Joomla extension URL:{' '}
          <a
            href="https://github.com/CrazyEggInc/crazyegg-joomla-plugin/raw/master/crazyegg-joomla-plugin.zip"
            target="_blank"
            rel="noopener noreferrer"
            className="font-bold text-dodger-blue-500 hover:underline break-all"
          >
            https://github.com/CrazyEggInc/crazyegg-joomla-plugin/raw/master/crazyegg-joomla-plugin.zip
          </a>
        </ListItem>

        <ListItem number={4}>
          Click on <strong>Check and Install.</strong> Go to <strong>Extensions &gt; Plugins.</strong>
        </ListItem>

        <ListItem number={5}>
          Type <strong>Crazy Egg</strong> into the search field. Find the Crazy Egg plugin in the list and open it.
        </ListItem>

        <ListItem number={6}>
          Change the plugin&apos;s status to <strong>Enabled.</strong>
        </ListItem>

        <ListItem number={7}>
          Enter your Crazy Egg account number, <strong>{accNumber}</strong> into the Account Number field.
        </ListItem>
      </ol>

      <CodeSnippet code={accNumber} />

      <ol className="flex flex-col gap-4 mt-4">
        <ListItem number={8}>
          Click on <strong>Save.</strong>
        </ListItem>
      </ol>

      <div className="my-5 rounded bg-white-lilac-500 px-6 py-5 text-black-pearl-500">
        <strong className="text-body-1 text-black-pearl-500">Note:</strong> If you are using a cache plugin on your
        site, you will need to clear it after you have installed your Crazy Egg code.
      </div>

      <p className="mt-10 text-base font-normal leading-6 text-black-pearl-500 mb-3">
        Watch this video to see these steps:
      </p>

      <VideoPlayer src={JoomlaVideo} poster={JoomlaPoster} label="Joomla installation video" />

      <div className="flex flex-col gap-1 mt-3">
        <SupportLink
          href={SupportLinks.install.joomla}
          className="text-sm sm:text-base font-bold leading-6 text-dodger-blue-500 hover:underline"
        >
          Joomla Setup Guide
        </SupportLink>
        <SupportLink
          href={SupportLinks.general.newSupportRequest}
          className="text-sm sm:text-base font-bold leading-6 text-dodger-blue-500 hover:underline"
        >
          Contact Support
        </SupportLink>
      </div>

      <p className="mt-3 text-black-pearl-500">
        Click on <strong>Check installation</strong> once you have connected Crazy Egg with Joomla.
      </p>
    </Card>
  );
}
